import {
    EEAeNode,
    EEFvNode,
    EENotebox,
} from '../static-diagram/static-diagram-graph-d3/d3-utils/d3-models.util';
import { AeBoxAnchor } from 'src/models/anchor-and-version/ae-box.model';
import { AllocationElementDiagramAnchor } from 'src/models/anchor-and-version/allocation-element-diagram.model';
import { BaseAnchorAndVersion } from 'src/models/anchor-and-version/base.model';
import { FvBoxAnchor } from 'src/models/anchor-and-version/fv-box.model';
import { FvBoxPortDataRelationAnchor } from 'src/models/anchor-and-version/fv-box-port-data-relation.model';
import { GatewayAnchor } from 'src/models/anchor-and-version/gateway.model';
import { NEW_NODE_OFFSET } from './const.util';
import { NoteBoxAnchor } from 'src/models/anchor-and-version/note-box.model';
import { createAeBoxAnchor } from 'src/factories/anchor-and-version/ae-box.factory';
import { createAllocationElementDiagramAnchor } from 'src/factories/anchor-and-version/allocation-element-diagram.factory';
import { createFvBoxAnchor } from 'src/factories/anchor-and-version/fv-box.factory';
import { createFvBoxPortDataRelationAnchor } from 'src/factories/anchor-and-version/fv-box-port-data-relation.factory';
import { createGatewayAnchor } from 'src/factories/anchor-and-version/gateway.factory';
import { createNoteBoxAnchor } from 'src/factories/anchor-and-version/note-box.factory';

interface DuplicateBoxAnchorOptions {
    positionOffset?: number;
}

export function getFvType(fvNode: EEFvNode): 'can' | 'directWire' | 'internal' {
    const relations = fvNode.data.fvBoxPortDataRelationAnchors ?? [];

    if (relations.some((relation) => relation.canPortDataAnchor || relation.canPortDataAnchorKey)) {
        return 'can';
    }
    if (
        relations.some(
            (relation) => relation.directWirePortDataAnchor || relation.directWirePortDataAnchorKey
        )
    ) {
        return 'directWire';
    }
    if (
        relations.some(
            (relation) => relation.internalPortDataAnchor || relation.internalPortDataAnchorKey
        )
    ) {
        return 'internal';
    }

    if (
        fvNode.data.sourceAddressAnchorKey ||
        fvNode.data.targetAddressAnchorKey ||
        fvNode.data.gatewayAnchors?.length
    ) {
        return 'can';
    }

    return 'can';
}

export function createDuplicatedAeBoxAnchor(
    original: AeBoxAnchor,
    aedAnchorKey: number,
    options: DuplicateBoxAnchorOptions = {}
): AeBoxAnchor | null {
    const originalVersion = original.validVersion;
    if (!originalVersion) return null;

    const positionOffset = options.positionOffset ?? NEW_NODE_OFFSET;
    const copiedAnchor = createAeBoxAnchor(
        {
            aeAnchorKey: original.aeAnchorKey,
            aedAnchorKey,
            logText: original.logText,
        },
        {
            comment: originalVersion.comment,
            height: originalVersion.height,
            logText: originalVersion.logText,
            name: originalVersion.name,
            width: originalVersion.width,
            x: originalVersion.x + positionOffset,
            y: originalVersion.y + positionOffset,
            z: originalVersion.z,
        }
    );

    copiedAnchor.displayName = original.displayName;
    copiedAnchor.allocationElementAnchor = original.allocationElementAnchor;
    copiedAnchor.rxFvBoxAnchors = [];
    copiedAnchor.txFvBoxAnchors = [];

    return copiedAnchor;
}

export function createDuplicatedAllocationElementDiagramAnchor(
    original: AllocationElementDiagramAnchor
): AllocationElementDiagramAnchor | null {
    const originalVersion = original.validVersion;
    if (!originalVersion) return null;

    const copiedName = `Copy of ${originalVersion.name}`;
    const copiedAnchor = createAllocationElementDiagramAnchor(
        {
            logText: original.logText,
            userFunctionAnchorKey: original.userFunctionAnchorKey,
        },
        {
            comment: originalVersion.comment,
            logText: originalVersion.logText,
            name: copiedName,
        }
    );

    copiedAnchor.displayName = copiedName;
    copiedAnchor.aeBoxAnchors = [];
    copiedAnchor.noteBoxAnchors = [];

    return copiedAnchor;
}

export function createDuplicatedFvBoxAnchor(
    original: FvBoxAnchor,
    options: DuplicateBoxAnchorOptions = {}
): FvBoxAnchor | null {
    const originalVersion = original.validVersion;
    if (!originalVersion) return null;

    const positionOffset = options.positionOffset ?? NEW_NODE_OFFSET;
    const relationCopies = (original.fvBoxPortDataRelationAnchors ?? [])
        .map(createDuplicatedFvBoxPortDataRelationAnchor)
        .filter(Boolean) as FvBoxPortDataRelationAnchor[];
    const gatewayCopies = (original.gatewayAnchors ?? [])
        .map(createDuplicatedGatewayAnchor)
        .filter(Boolean) as GatewayAnchor[];

    if (
        (original.fvBoxPortDataRelationAnchors?.length ?? 0) !== relationCopies.length ||
        (original.gatewayAnchors?.length ?? 0) !== gatewayCopies.length
    ) {
        return null;
    }

    const copiedAnchor = createFvBoxAnchor(
        {
            logText: original.logText,
            sourceAeBoxAnchorKey: original.sourceAeBoxAnchorKey,
            targetAeBoxAnchorKey: original.targetAeBoxAnchorKey,
            sourceAddressAnchorKey: original.sourceAddressAnchorKey,
            targetAddressAnchorKey: original.targetAddressAnchorKey,
            sourceAddressAnchor: original.sourceAddressAnchor,
            targetAddressAnchor: original.targetAddressAnchor,
            gatewayAnchors: gatewayCopies,
            fvBoxPortDataRelationAnchors: relationCopies,
        },
        {
            comment: originalVersion.comment,
            height: originalVersion.height,
            logText: originalVersion.logText,
            name: originalVersion.name,
            width: originalVersion.width,
            x: originalVersion.x + positionOffset,
            y: originalVersion.y + positionOffset,
            z: originalVersion.z,
        }
    );

    copiedAnchor.displayName = original.displayName;
    return copiedAnchor;
}

export function createDuplicatedNoteBoxAnchor(
    original: NoteBoxAnchor,
    aedAnchorKey: number,
    options: DuplicateBoxAnchorOptions = {}
): NoteBoxAnchor | null {
    const originalVersion = original.validVersion;
    if (!originalVersion) return null;

    const positionOffset = options.positionOffset ?? NEW_NODE_OFFSET;
    const copiedAnchor = createNoteBoxAnchor(
        {
            aedAnchorKey,
            logText: original.logText,
        },
        {
            comment: originalVersion.comment,
            dock: originalVersion.dock,
            height: originalVersion.height,
            logText: originalVersion.logText,
            name: originalVersion.name,
            text: originalVersion.text,
            width: originalVersion.width,
            x: originalVersion.x + positionOffset,
            y: originalVersion.y + positionOffset,
            z: originalVersion.z,
        }
    );

    copiedAnchor.displayName = original.displayName;
    return copiedAnchor;
}

function createDuplicatedGatewayAnchor(original: GatewayAnchor): GatewayAnchor | null {
    const originalVersion = original.validVersion;
    const segmentAnchorKey = original.segmentAnchorKey ?? original.segmentAnchor?.key;
    if (!originalVersion || segmentAnchorKey == null) return null;

    return createGatewayAnchor(
        {
            logText: original.logText,
            componentAnchorKey:
                original.componentAnchorKey ?? original.componentAnchor?.key ?? undefined,
            fvBoxAnchorKey: 0,
            segmentAnchorKey,
            position: original.position,
            componentAnchor: original.componentAnchor ?? undefined,
            segmentAnchor: original.segmentAnchor ?? undefined,
        },
        {
            changeRequestKey: 1,
            comment: originalVersion.comment,
            isActive: false,
            logText: originalVersion.logText,
            name: originalVersion.name,
        }
    );
}
function createDuplicatedFvBoxPortDataRelationAnchor(
    original: FvBoxPortDataRelationAnchor
): FvBoxPortDataRelationAnchor | null {
    const originalVersion = original.validVersion;
    if (!originalVersion) return null;

    const canPortDataAnchorKey = original.canPortDataAnchorKey ?? original.canPortDataAnchor?.key;
    const directWirePortDataAnchorKey =
        original.directWirePortDataAnchorKey ?? original.directWirePortDataAnchor?.key;
    const internalPortDataAnchorKey =
        original.internalPortDataAnchorKey ?? original.internalPortDataAnchor?.key;

    if (
        canPortDataAnchorKey == null &&
        directWirePortDataAnchorKey == null &&
        internalPortDataAnchorKey == null
    ) {
        return null;
    }

    return createFvBoxPortDataRelationAnchor(
        {
            logText: original.logText,
            fvBoxAnchorKey: 0,
            canPortDataAnchorKey: canPortDataAnchorKey ?? undefined,
            directWirePortDataAnchorKey: directWirePortDataAnchorKey ?? undefined,
            internalPortDataAnchorKey: internalPortDataAnchorKey ?? undefined,
            canPortDataAnchor: original.canPortDataAnchor ?? undefined,
            directWirePortDataAnchor: original.directWirePortDataAnchor ?? undefined,
            internalPortDataAnchor: original.internalPortDataAnchor ?? undefined,
        },
        {
            changeRequestKey: 1,
            comment: originalVersion.comment,
            isRxActive: false,
            isTxActive: false,
            logText: originalVersion.logText,
            name: originalVersion.name,
        }
    );
}
export function getElementForObject(obj: EEAeNode | EEFvNode | EENotebox): Element | null {
    const prefix =
        obj?.type === 'notebox'
            ? 'notebox'
            : obj?.type === 'node' && obj?.nodeType === 'ae'
              ? 'ae'
              : obj?.type === 'node' && obj?.nodeType === 'fv'
                ? 'fv'
                : null;
    return prefix ? document.querySelector(`g#${prefix}-${obj.data.key}`) : null;
}

export function getObjectBounds(obj: EEAeNode | EEFvNode | EENotebox) {
    const element = getElementForObject(obj);
    if (element) {
        return getElementBounds(element);
    }
    const version = obj.data.validVersion;
    if (!version) return null;
    return {
        x: version.x || 0,
        y: version.y || 0,
        width: version.width || 0,
        height: version.height || 0,
    };
}

export function getElementBounds(element: Element) {
    const rect = element.querySelector('rect');
    if (!rect) return null;

    const transform = element.getAttribute('transform');
    let x = 0,
        y = 0;

    if (transform) {
        const translateMatch = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
        if (translateMatch) {
            x = parseFloat(translateMatch[1]);
            y = parseFloat(translateMatch[2]);
        }
    }

    return {
        x,
        y,
        width: parseFloat(rect.getAttribute('width') || '0'),
        height: parseFloat(rect.getAttribute('height') || '0'),
    };
}

export function isOverlapping(
    bounds1: { x: number; y: number; width: number; height: number },
    bounds2: { x: number; y: number; width: number; height: number }
): boolean {
    return !(
        bounds1.x + bounds1.width <= bounds2.x ||
        bounds2.x + bounds2.width <= bounds1.x ||
        bounds1.y + bounds1.height <= bounds2.y ||
        bounds2.y + bounds2.height <= bounds1.y
    );
}

export function getOverlappingElements(obj: EEAeNode | EEFvNode | EENotebox): Element[] {
    const objBounds = getObjectBounds(obj);
    if (!objBounds) return [];

    const targetElement = getElementForObject(obj);
    const allElements = document.querySelectorAll('g[id^="ae-"], g[id^="fv-"], g[id^="notebox-"]');

    return Array.from(allElements).filter((element) => {
        if (element === targetElement) return false;
        const elementBounds = getElementBounds(element);
        return elementBounds && isOverlapping(objBounds, elementBounds);
    });
}

export function getNextTemporaryAnchorKey(anchors: { key: number }[]): number {
    const temporaryKeys = anchors.map((anchor) => anchor.key).filter((key) => key < 0);

    return temporaryKeys.length > 0 ? Math.min(...temporaryKeys) - 1 : -1;
}

export function mergeNewAndExistingObjects<T extends BaseAnchorAndVersion>(obj1: T, obj2: T): void {
    for (const property in obj1) {
        let newVal: unknown = obj1[property];
        if (property === 'key') {
            newVal = obj2.key;
        } else if (property === 'createdBy') {
            newVal = obj2.createdBy;
        } else if (property === 'createdTimestamp') {
            newVal = obj2.createdTimestamp;
        } else if (property === 'allVersions') {
            newVal = null;
        } else if (property === 'validVersion') {
            if ('allVersions' in obj2) {
                newVal = (obj2['allVersions'] as []).at(-1);
            }
        } else if (
            typeof obj1[property] === 'object' &&
            typeof obj2[property] === 'object' &&
            obj1[property] &&
            obj2[property]
        ) {
            if (Array.isArray(obj1[property])) {
                for (let i = 0; i < (obj1[property] as []).length; i++) {
                    mergeNewAndExistingObjects(
                        (obj1[property] as [])[i] as T,
                        (obj2[property] as [])[i] as T
                    );
                }
            } else {
                mergeNewAndExistingObjects(obj1[property] as T, obj2[property] as T);
            }
        }

        obj1[property] = newVal as T[Extract<keyof T, string>];
    }
}

export function removeNestedObjects<T extends BaseAnchorAndVersion>(obj: T): T | undefined {
    if (obj.key !== 0) {
        return undefined;
    }

    for (const key in obj) {
        const value = obj[key];
        if (typeof value === 'object' && value) {
            obj[key] = (
                key === 'validVersion'
                    ? undefined
                    : Array.isArray(value)
                      ? (value as []).map((item) => removeNestedObjects(item as T)).filter(Boolean)
                      : removeNestedObjects(value as T)
            ) as T[Extract<keyof T, string>];
        }
    }

    return obj;
}

export function updateOrAddVersion<T extends { anchorKey: number }>(
    versions: T[],
    newVersion: T,
    anchorKey: number
): void {
    const existingIndex = versions.findIndex((v) => v.anchorKey === anchorKey);
    if (existingIndex >= 0) {
        versions[existingIndex] = newVersion;
    } else {
        versions.push(newVersion);
    }
}
